import { useQuery } from "@tanstack/react-query";
import { PRODUCT_ORDER, type AppLink, type AppLinks, type ProductKey } from "@optymyse/suite-ui";
import { api, ApiError, jsonOrThrow } from "./api";
import { PRODUCT_KEY } from "../../shared/product";

/**
 * The person's apps in the workspace in view, from Optymyse through Link's API (`GET /api/v1/apps`):
 * the list behind the squares in the top bar. Only the list; whether the person may use an app is
 * decided where they open it. Kept as Steer's and Sustain's consoles keep theirs: used for five minutes,
 * then asked for again when the page is next in view. After a network or server failure it is asked
 * for twice more, one second and then two seconds later; a refusal won't change by asking again. What
 * the squares show meanwhile is `squares`.
 */
export type SuiteApp = { product: string; url: string };

export function useApps() {
  return useQuery({
    queryKey: ["apps"],
    queryFn: async () => (await jsonOrThrow<{ apps: SuiteApp[] }>(await api.api.v1.apps.$get())).apps,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, err) => !(err instanceof ApiError && err.status < 500) && failureCount < 2,
    retryDelay: (failureCount) => 1_000 * 2 ** failureCount,
  });
}

/**
 * What the squares show, from the first frame (SuiteUi's AppLinks): the list once Optymyse's has come,
 * "loading" until then, and "unavailable" once a network or server failure has had its retries. A
 * refusal (a 4xx) won't change by asking again, so Link is alone in them.
 */
export function squares(apps: Pick<ReturnType<typeof useApps>, "data" | "error">): AppLinks {
  if (apps.data) return appLinks(apps.data);
  if (!apps.error) return "loading";
  return apps.error instanceof ApiError && apps.error.status < 500 ? appLinks([]) : "unavailable";
}

/**
 * Link, where the person is, and the other apps Optymyse lists for them here, in the suite's order, which the kit keeps
 * (`PRODUCT_ORDER`, Link sixth). An app the kit has no mark for is left out. With no other app, Link is alone in the
 * squares.
 */
export function appLinks(listed: readonly SuiteApp[]): AppLink[] {
  const known = (product: string): product is ProductKey => (PRODUCT_ORDER as readonly string[]).includes(product);
  const others = new Map(
    listed.flatMap(({ product, url }) => (known(product) && product !== PRODUCT_KEY ? [[product, url] as const] : [])),
  );
  return PRODUCT_ORDER.flatMap((product): AppLink[] => {
    if (product === PRODUCT_KEY) return [{ product, href: "/admin" }];
    const href = others.get(product);
    return href === undefined ? [] : [{ product, href }];
  });
}
